import React, {
  useCallback,
  useRef,
  useState,
  RefObject,
  MutableRefObject,
} from "react";
import {
  FailureTypeValue,
  GeneralStatusValue,
  MESSAGE_ACTION,
  MESSAGE_STATUS,
  MessageType,
  OnResponseTypeValue,
  SuccessTypeValue,
} from "../../types";
import { usePaymentContext } from "../../../../contexts/payment-context";
import { PromiseRefType } from "../paymentus-iframe";
import { IFrameObject } from "iframe-resizer-react";
import { logErrorMessage } from "../../../../utils";
import {
  setIsPending,
  setIsSetAsDefaultChecked,
  setIsFirstRegistrationPending,
} from "../../../../contexts/payment-context/state";
import { WalletCheckboxValue } from "../../../../types";

type OnMessageReceivedFromIframeProps = { message: MessageType };

type UseIframeInitialization = {
  iframeRef: RefObject<IFrameObject>;
  isIframeReady: boolean;
  onMessageReceivedFromIframe: ({
    message,
  }: OnMessageReceivedFromIframeProps) => void;
  onInitIframeHandler: () => void;
  promise: RefObject<PromiseRefType>;
  isSetAsDefaultCheckedRef: MutableRefObject<boolean | undefined>;
};

export const useIframeInitialization = (): UseIframeInitialization => {
  const {
    brand,
    dispatch,
    onFailure,
    onResponse,
    onSuccess,
    applicationName,
    owner,
    formsSetup,
    setWalletCheckboxProps,
    state: { isSetAsDefaultChecked, isFirstRegistrationPending },
    subbrand,
    updateDefaultPaymentMethod,
  } = usePaymentContext();

  const promise = React.useRef<PromiseRefType>({});
  const iframeRef = useRef<IFrameObject>(null);
  const [isIframeReady, setIframeReady] = useState(false);
  const isSetAsDefaultCheckedRef = useRef<boolean>(!!isSetAsDefaultChecked);
  const isFirstRegistrationPendingRef = useRef<boolean>(
    !!isFirstRegistrationPending
  );

  const onMessageReceivedFromIframe = useCallback(
    async ({ message }: OnMessageReceivedFromIframeProps) => {
      switch (message.status) {
        case `${MESSAGE_STATUS.PAYMENTUS_REGISTRATION_SUCCESS}`:
          setIframeReady(true);
          // The "Set as Default" switch will be set automatically on "ON" only if all conditions are satisfied:
          // 1. MFE shows "Set As Default" checkbox.
          // 2. The `defaultChecked` prop is not provided by the host application.
          // 3. No default payment method exists for the account.
          // 4. This is first registration call.

          if (
            setWalletCheckboxProps?.showWalletCheckbox ===
              WalletCheckboxValue.SET_AS_DEFAULT &&
            setWalletCheckboxProps?.defaultChecked === undefined &&
            isFirstRegistrationPendingRef.current
          ) {
            dispatch(
              setIsSetAsDefaultChecked(
                !message?.data?.registration_data?.defaultPaymentMethodExists
              )
            );
          }
          onResponse({
            type: OnResponseTypeValue.REGISTRATION_SUCCESS,
            registrationSuccessResponse: message?.data?.registration_data,
          });
          dispatch(setIsFirstRegistrationPending(false));
          isFirstRegistrationPendingRef.current = false;
          break;
        case `${MESSAGE_STATUS.PAYMENTUS_REGISTRATION_ERROR}`:
          setIframeReady(true);
          onFailure({
            type: FailureTypeValue.PAYMENTUS_FAILURE,
            paymentusFailureResponse: message?.error?.registration_error,
          });
          logErrorMessage(message?.error?.registration_error?.uuid);

          break;
        case `${MESSAGE_STATUS.PAYMENTUS_FORM_INVALID_FIELDS}`: {
          const formError = {
            type: FailureTypeValue.NOT_VALID_PAYMENTUS_FORM,
            paymentusFormErrors: message?.error?.paymentus_form_error,
          };
          onFailure(formError);
          dispatch(setIsPending(false));
          promise?.current?.resolve?.({
            paymentus: formError,
            generalStatus: GeneralStatusValue.PAYMENT_FAILED,
          });
          break;
        }
        case `${MESSAGE_STATUS.PAYMENT_SECURE_SERVICE_SUCCESS}`:
          onResponse({
            type: OnResponseTypeValue.TOKENIZATION_SUCCESS,
            tokenizationSuccessResponse:
              message?.data?.payment_secure_service_data,
          });
          break;
        case `${MESSAGE_STATUS.PAYMENT_SECURE_SERVICE_ERROR}`: {
          setIframeReady(true);
          const secureServiceError = {
            type: FailureTypeValue.PAYMENTUS_FAILURE,
            paymentusFailureResponse:
              message?.error?.payment_secure_service_error,
          };
          onFailure(secureServiceError);
          logErrorMessage(message?.error?.payment_secure_service_error?.uuid);
          dispatch(setIsPending(false));
          promise?.current?.resolve?.({
            paymentus: secureServiceError,
            generalStatus: GeneralStatusValue.PAYMENT_FAILED,
          });
          break;
        }
        case `${MESSAGE_STATUS.CREATE_PAYMENT_METHOD_SUCCESS}`: {
          const finalResult = {
            type: SuccessTypeValue.PAYMENTUS_SUCCESS,
            paymentusSuccessResponse: message?.data?.create_payment_method_data,
          };
          onResponse(finalResult);
          onSuccess(finalResult);

          if (isSetAsDefaultCheckedRef.current) {
            const setAsDefaultResponse = await updateDefaultPaymentMethod({
              contractID: setWalletCheckboxProps?.contractID,
              customerID: setWalletCheckboxProps?.customerID,
              dataStore: {
                id:
                  message?.data?.create_payment_method_data?.paymentMethod
                    ?.paymentMethodID,
                dataLocation: 3,
              },
              propertyID: setWalletCheckboxProps?.propertyID,
              sourceTypeCode: setWalletCheckboxProps?.sourceTypeCode || "CSC",
            });
            dispatch(setIsPending(false));
            promise?.current?.resolve?.({
              paymentus: finalResult,
              setAsDefault: setAsDefaultResponse,
              generalStatus:
                setAsDefaultResponse?.status ||
                GeneralStatusValue.PAYMENT_SUCCESS,
            });
          } else {
            dispatch(setIsPending(false));
            promise?.current?.resolve?.({
              paymentus: finalResult,
              generalStatus: GeneralStatusValue.PAYMENT_SUCCESS,
            });
          }
          break;
        }
        case `${MESSAGE_STATUS.CREATE_PAYMENT_METHOD_ERROR}`: {
          const paymentMethodError = {
            type: FailureTypeValue.PAYMENTUS_FAILURE,
            paymentusFailureResponse:
              message?.error?.create_payment_method_error,
          };
          onFailure(paymentMethodError);
          logErrorMessage(message?.error?.create_payment_method_error?.uuid);
          dispatch(setIsPending(false));
          promise?.current?.resolve?.({
            paymentus: paymentMethodError,
            generalStatus: GeneralStatusValue.PAYMENT_FAILED,
          });
          break;
        }
        case `${MESSAGE_STATUS.CREATE_PAYMENT_METHOD_REDIS_CACHE_ERROR}`: {
          const paymentMethodError = {
            type: FailureTypeValue.PAYMENTUS_FAILURE,
            paymentusFailureResponse:
              message?.error?.create_payment_method_error,
          };
          onFailure(paymentMethodError);
          logErrorMessage(message?.error?.create_payment_method_error?.uuid);
          break;
        }
      }
    },
    [
      dispatch,
      onFailure,
      onResponse,
      onSuccess,
      setWalletCheckboxProps?.contractID,
      setWalletCheckboxProps?.customerID,
      setWalletCheckboxProps?.defaultChecked,
      setWalletCheckboxProps?.propertyID,
      setWalletCheckboxProps?.showWalletCheckbox,
      setWalletCheckboxProps?.sourceTypeCode,
      updateDefaultPaymentMethod,
    ]
  );

  const onInitIframeHandler = useCallback(() => {
    iframeRef?.current!.sendMessage({
      action: MESSAGE_ACTION.PAYMENTUS_FORM_CONFIG,
      data: {
        paymentus_form_config: formsSetup?.paymentusForm,
      },
    });

    iframeRef?.current!.sendMessage({
      action: MESSAGE_ACTION.TRIGGER_REGISTRATION,
      data: {
        registration_request_data: {
          paymentMethod: {
            brand: brand,
            ...(subbrand ? { subbrand } : {}),
            owner: owner,
            source: {
              name: applicationName,
            },
          },
          ...(setWalletCheckboxProps?.defaultChecked === undefined &&
          setWalletCheckboxProps?.showWalletCheckbox ===
            WalletCheckboxValue.SET_AS_DEFAULT &&
          isFirstRegistrationPending
            ? {
                defaultCheck: {
                  checkDefault: true,
                  contractID: setWalletCheckboxProps?.contractID,
                },
              }
            : {}),
        },
      },
    });
  }, [
    applicationName,
    brand,
    isFirstRegistrationPending,
    owner,
    formsSetup?.paymentusForm,
    setWalletCheckboxProps?.contractID,
    setWalletCheckboxProps?.defaultChecked,
    setWalletCheckboxProps?.showWalletCheckbox,
    subbrand,
  ]);

  return {
    iframeRef,
    isIframeReady,
    onMessageReceivedFromIframe,
    onInitIframeHandler,
    promise,
    isSetAsDefaultCheckedRef,
  };
};
